import React from 'react';
import { X } from 'lucide-react';

// 行程規劃使用教學：8 個步驟，每步一句說明＋統一 3:4 截圖
// 截圖按語言分開：繁體 public/guide/step1–8.jpg，其餘 public/guide/<lang>/step1–8.jpg
const TXT = {
  'zh-HK': {
    title: '行程規劃使用教學',
    sub: '只需 3 步，AI 就會幫你安排每日行程。只有第 1 步是必填，其餘可以跳過。',
    close: '開始規劃',
    steps: [
      ['填目的地', '填上國家、城市和出發日期。去多個城市，按「＋ 增加目的地」。'],
      ['填回程日期', '填了回程日期，天數會自動計算；不填就直接選天數。'],
      ['加入想去的景點', '輸入一定要去的地方，按「＋」加入。可跳過。'],
      ['選活動', '按下想去的活動類型。選了 Outlet 或溫泉，再挑一個地點。可跳過。'],
      ['選旅遊風格', '選 1 至 3 個你喜歡的風格，例如美食、購物、娛樂。'],
      ['選交通和同行者', '選公共交通或自駕，再選個人、好友或親子遊。'],
      ['按「一鍵計劃」', '確認黃色摘要無誤後按下，稍等片刻就會生成行程。'],
      ['查看和修改', '按「換一批」替換景點；滿意就儲存、分享或下載 PDF。'],
    ],
  },
  'zh-CN': {
    title: '行程规划使用教学',
    sub: '只需 3 步，AI 就会帮你安排每日行程。只有第 1 步是必填，其余可以跳过。',
    close: '开始规划',
    steps: [
      ['填目的地', '填上国家、城市和出发日期。去多个城市，按「＋ 增加目的地」。'],
      ['填回程日期', '填了回程日期，天数会自动计算；不填就直接选天数。'],
      ['加入想去的景点', '输入一定要去的地方，按「＋」加入。可跳过。'],
      ['选活动', '按下想去的活动类型。选了 Outlet 或温泉，再挑一个地点。可跳过。'],
      ['选旅游风格', '选 1 至 3 个你喜欢的风格，例如美食、购物、娱乐。'],
      ['选交通和同行者', '选公共交通或自驾，再选个人、好友或亲子游。'],
      ['按「一键计划」', '确认黄色摘要无误后按下，稍等片刻就会生成行程。'],
      ['查看和修改', '按「换一批」替换景点；满意就储存、分享或下载 PDF。'],
    ],
  },
  'en': {
    title: 'How to plan a trip',
    sub: 'Three steps and the AI plans each day for you. Only step 1 is required; the rest can be skipped.',
    close: 'Start planning',
    steps: [
      ['Add your destination', 'Enter the country, city and departure date. Visiting several cities? Tap "+ Add Destination".'],
      ['Add a return date', 'With a return date the trip length is worked out for you; otherwise just pick the number of days.'],
      ['Add must-see spots', 'Type a place you want to visit and tap "+". Optional.'],
      ['Pick activities', 'Tap the activity types you like. For Outlet or hot springs, pick a specific place. Optional.'],
      ['Choose a travel style', 'Choose 1 to 3 styles, such as food, shopping or entertainment.'],
      ['Transport and companions', 'Pick public transport or self-drive, then solo, friends or family.'],
      ['Tap "Plan My Trip"', 'Check the yellow summary, tap the button and your itinerary is ready in a moment.'],
      ['Review and adjust', 'Tap "Swap for another batch" to change a spot. Happy with it? Save, share or download a PDF.'],
    ],
  },
  'ja': {
    title: '旅程作成ガイド',
    sub: '3 ステップで AI が毎日の旅程を作成します。必須はステップ 1 のみ、ほかはスキップできます。',
    close: '作成を始める',
    steps: [
      ['目的地を入力', '国・都市・出発日を入力。複数都市なら「＋ 目的地を追加」。'],
      ['帰国日を入力', '帰国日を入れると日数を自動計算。入れない場合は日数を選択。'],
      ['行きたいスポットを追加', '必ず行きたい場所を入力して「＋」。任意です。'],
      ['アクティビティを選ぶ', '好きな種類をタップ。アウトレットや温泉は場所も選択。任意です。'],
      ['旅行スタイルを選ぶ', 'グルメ・ショッピング・エンタメなど 1〜3 個選択。'],
      ['交通手段と同行者', '公共交通か車を選び、一人旅・友人・家族から選択。'],
      ['「ワンタップ計画」をタップ', '黄色の概要を確認してタップすると、まもなく旅程ができあがります。'],
      ['確認と調整', '「別の候補に入れ替え」でスポットを変更。保存・共有・PDF 出力もできます。'],
    ],
  },
  'ko': {
    title: '일정 만들기 안내',
    sub: '3단계면 AI가 매일 일정을 짜 드립니다. 1단계만 필수이고 나머지는 건너뛸 수 있습니다.',
    close: '일정 만들기',
    steps: [
      ['목적지 입력', '국가, 도시, 출발일을 입력하세요. 여러 도시라면 「＋ 목적지 추가」.'],
      ['귀국일 입력', '귀국일을 넣으면 일수가 자동 계산됩니다. 없으면 일수를 직접 선택.'],
      ['가고 싶은 명소 추가', '꼭 가고 싶은 곳을 입력하고 「＋」. 선택 사항입니다.'],
      ['액티비티 선택', '원하는 유형을 누르세요. 아울렛·온천은 장소도 선택. 선택 사항입니다.'],
      ['여행 스타일 선택', '맛집, 쇼핑, 엔터테인먼트 등 1~3개를 고르세요.'],
      ['교통과 동행', '대중교통 또는 자가용, 그리고 혼자·친구·가족을 선택.'],
      ['「원클릭 계획」 누르기', '노란 요약을 확인하고 누르면 곧 일정이 완성됩니다.'],
      ['확인 및 수정', '「다른 후보로 교체」로 명소를 바꾸고, 저장·공유·PDF 다운로드.'],
    ],
  },
};

export const TripGuideModal = ({ lang = 'zh-HK', onClose }) => {
  const L = TXT[lang] || TXT['en'];
  // 截圖跟語言：繁體在 /guide，其餘在 /guide/<lang>，未知語言用英文
  const imgDir = lang === 'zh-HK' ? '/guide' : `/guide/${TXT[lang] ? lang : 'en'}`;
  return (
    <div onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.55)', backdropFilter:'blur(8px)', padding:16 }}>
      <div onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true" aria-label={L.title}
        style={{ background:'white', borderRadius:20, width:'100%', maxWidth:880, maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 64px rgba(0,0,0,0.3)', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, padding:'18px 20px 12px', borderBottom:'1px solid #f1f5f9' }}>
          <div>
            <p style={{ margin:0, fontSize:18, fontWeight:900, color:'#0f172a' }}>{L.title}</p>
            <p style={{ margin:'4px 0 0', fontSize:12, color:'#64748b', lineHeight:1.6 }}>{L.sub}</p>
          </div>
          <button onClick={onClose} aria-label="close"
            style={{ background:'#f1f5f9', border:'none', borderRadius:10, width:34, height:34, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b', flexShrink:0 }}>
            <X size={16}/>
          </button>
        </div>
        <div style={{ overflowY:'auto', padding:'16px 20px 20px' }}>
          {/* 步驟卡片：同一比例截圖，按順序排列 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))', gap:14 }}>
            {L.steps.map(([name, desc], i) => (
              <div key={i} style={{ border:'1.5px solid #e2e8f0', borderRadius:14, overflow:'hidden', display:'flex', flexDirection:'column', background: i===6 ? '#fffbeb' : 'white' }}>
                <div style={{ padding:'10px 12px 8px', display:'flex', alignItems:'flex-start', gap:8 }}>
                  <span style={{ width:22, height:22, borderRadius:'50%', background: i===6 ? '#f59e0b' : '#0f172a', color:'white', fontSize:11, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+1}</span>
                  <div>
                    <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#0f172a', lineHeight:1.4 }}>{name}</p>
                    <p style={{ margin:'3px 0 0', fontSize:11, color:'#64748b', lineHeight:1.55 }}>{desc}</p>
                  </div>
                </div>
                <img src={`${imgDir}/step${i+1}.jpg`} alt={name} loading="lazy"
                  style={{ width:'100%', aspectRatio:'3 / 4', objectFit:'cover', display:'block', marginTop:'auto', borderTop:'1px solid #f1f5f9' }}/>
              </div>
            ))}
          </div>
          <button onClick={onClose}
            style={{ marginTop:18, width:'100%', background:'#0f172a', color:'white', border:'none', borderRadius:12, padding:'13px', fontSize:14, fontWeight:900, cursor:'pointer' }}>
            {L.close}
          </button>
        </div>
      </div>
    </div>
  );
};
